import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Divider,
  Fab,
  Chip,
} from '@mui/material';
import {
  AccountBalance,
  Send,
  TrendingUp,
  Add,
  Visibility,
  VisibilityOff,
  AccountBalanceWallet,
  ArrowForward,
  CreditCard,
  QrCode,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { fetchTransactions } from '../../store/slices/transactionsSlice';
import { useNavigate } from 'react-router-dom';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { transactions } = useSelector((state: RootState) => state.transactions);

  useEffect(() => {
    dispatch(fetchTransactions());
  }, [dispatch]);

  const quickActions = [
    {
      title: 'Send',
      icon: <Send sx={{ fontSize: 28 }} />,
      onClick: () => navigate('/send-money'),
    },
    {
      title: 'Cards',
      icon: <CreditCard sx={{ fontSize: 28 }} />,
      onClick: () => navigate('/cards'),
    },
    {
      title: 'QR Pay',
      icon: <QrCode sx={{ fontSize: 28 }} />,
      onClick: () => navigate('/qr-code'),
    },
  ];

  const recentTransactions = transactions.slice(0, 5).map((tx) => {
    let subtitle = '';
    try {
      const dateStr = (tx as any).created_at || tx.createdAt;
      subtitle = dateStr ? new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) : '';
    } catch { subtitle = ''; }
    return {
      id: tx.id,
      title: tx.description || (tx.type || '').replace('_', ' '),
      subtitle,
      amount: tx.type === 'transfer' ? -Number(tx.amount || 0) : Number(tx.amount || 0),
      type: tx.type,
      status: tx.status,
    };
  });

  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: 900 }, mx: 'auto' }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5, fontSize: '0.9rem' }}>
          Welcome back,
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#202647', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          {user?.name || user?.firstName || 'User'} {user?.lastName || ''}
        </Typography>
      </Box>

      {/* Quick Actions Card (replaces Balance Card) */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 50%, #09486D 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientShift 8s ease infinite',
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
          color: 'white',
          borderRadius: 4,
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
          border: 'none',
          '&:hover': { transform: 'none', boxShadow: '0 12px 40px rgba(9, 72, 109, 0.3)' },
        }}
      >
        {/* Decorative circles */}
        <Box sx={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -40,
          left: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
        }} />
        <Box sx={{
          position: 'absolute',
          top: '50%',
          right: -60,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.04)',
          transform: 'translateY(-50%)',
        }} />
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 }, position: 'relative', zIndex: 1 }}>
          {/* Quick Actions Label */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
            <AccountBalanceWallet sx={{ fontSize: 18, color: 'rgba(255,255,255,0.95)' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.875rem' }}>
              Quick Actions
            </Typography>
          </Box>

          {/* Quick Action Buttons */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-around',
            gap: { xs: 1, sm: 3 },
          }}>
            {quickActions.map((action, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)' },
                  '&:active': { transform: 'scale(0.95)' },
                  flex: 1,
                  maxWidth: 100,
                }}
                onClick={action.onClick}
              >
                <Box
                  sx={{
                    width: { xs: 52, sm: 60 },
                    height: { xs: 52, sm: 60 },
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.25)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  {action.icon}
                </Box>
                <Typography variant="body2" sx={{
                  color: 'rgba(255,255,255,0.95)',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  textAlign: 'center',
                }}>
                  {action.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      <Card sx={{ borderRadius: 4, mb: 3, border: 'none' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
              Recent Transactions
            </Typography>
            <Chip
              label="See all"
              size="small"
              onClick={() => navigate('/transactions')}
              icon={<ArrowForward sx={{ fontSize: 14 }} />}
              sx={{
                cursor: 'pointer',
                bgcolor: 'rgba(17, 193, 236, 0.08)',
                color: '#09486D',
                fontWeight: 600,
                fontSize: '0.75rem',
                '& .MuiChip-icon': { color: '#11C1EC', order: 1, ml: 0.5, mr: -0.5 },
                '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.15)' },
              }}
            />
          </Box>

          {recentTransactions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AccountBalanceWallet sx={{ fontSize: 48, color: '#D0D0DC', mb: 1 }} />
              <Typography variant="body1" sx={{ color: '#7F839C' }}>
                No transactions yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#B0B0C0' }}>
                Your transactions will appear here
              </Typography>
            </Box>
          ) : (
            recentTransactions.map((transaction, index) => (
              <React.Fragment key={transaction.id}>
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.03)' },
                  }}
                  onClick={() => navigate(`/transaction/${transaction.id}`)}
                >
                  {/* Transaction Icon */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: transaction.amount > 0
                        ? 'rgba(0, 196, 140, 0.1)'
                        : 'rgba(17, 193, 236, 0.1)',
                      mr: 2,
                    }}
                  >
                    {transaction.type === 'transfer' ? (
                      <Send sx={{
                        fontSize: 20,
                        color: '#11C1EC',
                        transform: 'rotate(-30deg)',
                      }} />
                    ) : (
                      <TrendingUp sx={{ fontSize: 20, color: '#00C48C' }} />
                    )}
                  </Box>

                  {/* Transaction Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{
                      fontWeight: 600,
                      color: '#272A3A',
                      fontSize: '0.9rem',
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {transaction.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B0B0C0', fontSize: '0.8rem' }}>
                      {transaction.subtitle}
                    </Typography>
                  </Box>

                  {/* Amount */}
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: transaction.amount > 0 ? '#00C48C' : '#FF4757',
                        fontSize: '0.95rem',
                      }}
                    >
                      {transaction.amount > 0 ? '+' : '-'}₦{Math.abs(transaction.amount).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                    </Typography>
                    <Chip
                      label={transaction.status}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        bgcolor: transaction.status === 'completed'
                          ? 'rgba(0, 196, 140, 0.1)'
                          : transaction.status === 'failed'
                            ? 'rgba(255, 71, 87, 0.1)'
                            : 'rgba(255, 185, 70, 0.1)',
                        color: transaction.status === 'completed'
                          ? '#00C48C'
                          : transaction.status === 'failed'
                            ? '#FF4757'
                            : '#FFB946',
                      }}
                    />
                  </Box>
                </Box>
                {index < recentTransactions.length - 1 && <Divider sx={{ mx: 3 }} />}
              </React.Fragment>
            ))
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card sx={{
          borderRadius: 4,
          border: 'none',
          background: 'linear-gradient(135deg, rgba(0, 196, 140, 0.08) 0%, rgba(0, 196, 140, 0.02) 100%)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#7F839C', fontWeight: 500, mb: 0.5 }}>
                  Income
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#00C48C' }}>
                  ₦0.00
                </Typography>
              </Box>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: 'rgba(0, 196, 140, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <TrendingUp sx={{ color: '#00C48C' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{
          borderRadius: 4,
          border: 'none',
          background: 'linear-gradient(135deg, rgba(255, 71, 87, 0.08) 0%, rgba(255, 71, 87, 0.02) 100%)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#7F839C', fontWeight: 500, mb: 0.5 }}>
                  Expenses
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF4757' }}>
                  ₦0.00
                </Typography>
              </Box>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: 'rgba(255, 71, 87, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Send sx={{ color: '#FF4757', transform: 'rotate(45deg)' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* FAB */}
      <Fab
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
          color: 'white',
          '&:hover': {
            background: 'linear-gradient(135deg, #0EAED6 0%, #07395A 100%)',
          },
        }}
        onClick={() => navigate('/send-money')}
      >
        <Send />
      </Fab>
    </Box>
  );
};

export default DashboardPage;
